import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UtensilsCrossed, Download, Sparkles, Loader2, RefreshCw } from "lucide-react";
import { exportDietPlanPDF } from "@/lib/pdf-export";

function mealKey(planId, mealIndex) {
  return `${planId}-${mealIndex}`;
}

function MacroBadges({ calories, protein, carbs, fat, className = "" }) {
  if (!calories && !protein && !carbs && !fat) return null;
  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {calories > 0 && <Badge className="bg-orange-50 text-orange-700 border-orange-100">{Math.round(calories)} kcal</Badge>}
      {protein > 0 && <Badge className="bg-blue-50 text-blue-700 border-blue-100">{protein}g protéines</Badge>}
      {carbs > 0 && <Badge className="bg-amber-50 text-amber-700 border-amber-100">{carbs}g glucides</Badge>}
      {fat > 0 && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100">{fat}g lipides</Badge>}
    </div>
  );
}

function MealAlternativeBlock({ plan, meal, mealIndex }) {
  const key = mealKey(plan.plan_id, mealIndex);
  const [state, setState] = useState({ loading: false, current: null, history: [] });

  const fetchAlternative = useCallback(async (isAnother = false) => {
    if (!meal.items?.length) {
      toast.error("Ce repas ne contient pas d'aliments");
      return;
    }

    let previousAlternatives = [];
    setState((prev) => {
      previousAlternatives = isAnother ? prev.history : [];
      return { ...prev, loading: true };
    });

    try {
      const { data } = await api.post("/ai/meal-alternative", {
        plan_id: plan.plan_id,
        meal_name: meal.name || `Repas ${mealIndex + 1}`,
        items: meal.items,
        previous_alternatives: previousAlternatives,
      });

      setState((prev) => ({
        loading: false,
        current: data,
        history: isAnother ? [...prev.history, data] : [data],
      }));
    } catch {
      setState((prev) => ({ ...prev, loading: false }));
      toast.error("Impossible de générer une alternative");
    }
  }, [meal, mealIndex, plan.plan_id]);

  return (
    <div className="bg-slate-50 rounded-xl p-4" data-testid={`diet-meal-${mealIndex}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
        <h4 className="font-semibold text-slate-900">{meal.name || `Repas ${mealIndex + 1}`}</h4>
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5 border-purple-200 text-purple-700 hover:bg-purple-50 shrink-0"
          onClick={() => fetchAlternative(false)}
          disabled={state.loading}
          data-testid={`meal-alt-btn-${mealIndex}`}
        >
          {state.loading && !state.current ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          Proposer alternative
        </Button>
      </div>

      <ul className="space-y-1.5">
        {meal.items?.map((item, j) => (
          <li key={j} className="flex items-center gap-2 text-sm text-slate-600">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
            {item}
          </li>
        ))}
      </ul>

      {state.current && (
        <div className="mt-4 rounded-xl border border-purple-100 bg-purple-50/60 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-purple-800 mb-3">
            <Sparkles className="w-4 h-4" />
            Alternative proposée
          </div>

          <ul className="space-y-1.5 mb-3">
            {state.current.items?.map((item, j) => (
              <li key={j} className="flex items-center gap-2 text-sm text-slate-700">
                <div className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>

          <MacroBadges
            calories={state.current.calories}
            protein={state.current.protein}
            carbs={state.current.carbs}
            fat={state.current.fat}
          />

          {state.current.original_macros && (
            <p className="text-xs text-slate-500 mt-3">
              Repas du plan estimé : {state.current.original_macros.calories} kcal ·{" "}
              {state.current.original_macros.protein}g P · {state.current.original_macros.carbs}g G ·{" "}
              {state.current.original_macros.fat}g L
            </p>
          )}

          <Button
            size="sm"
            variant="outline"
            className="mt-4 gap-1.5 border-purple-200 text-purple-700 hover:bg-purple-100"
            onClick={() => fetchAlternative(true)}
            disabled={state.loading}
            data-testid={`meal-alt-again-btn-${mealIndex}`}
          >
            {state.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Encore une proposition
          </Button>
        </div>
      )}
    </div>
  );
}

export default function MyDietPlan() {
  const [plans, setPlans] = useState([]);
  const [aiPdfLoading, setAiPdfLoading] = useState(null);

  const handleAiPdf = async (plan) => {
    setAiPdfLoading(plan.plan_id);
    try {
      const { data } = await api.post("/ai/pdf-analysis", { type: "diet" });
      exportDietPlanPDF(plan, data.analysis);
      toast.success("PDF avec analyse IA généré !");
    } catch {
      toast.error("Erreur lors de l'analyse IA");
    } finally {
      setAiPdfLoading(null);
    }
  };

  useEffect(() => {
    api.get("/diet-plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="my-diet-plan-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Mon Plan Diététique</h1>
      <p className="text-slate-500 mt-1">
        {plans.length} plan{plans.length > 1 ? "s" : ""} assigné{plans.length > 1 ? "s" : ""}
      </p>

      {plans.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-green-50 flex items-center justify-center mx-auto mb-4">
            <UtensilsCrossed className="w-8 h-8 text-green-400" />
          </div>
          <p className="text-slate-500">Aucun plan diététique assigné pour le moment.</p>
          <p className="text-sm text-slate-400 mt-1">Votre coach vous enverra votre plan personnalisé.</p>
        </div>
      ) : (
        <div className="space-y-6 mt-6">
          {plans.map((plan) => (
            <Card key={plan.plan_id} className="border-slate-100" data-testid={`diet-plan-${plan.plan_id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="font-heading text-2xl font-bold text-slate-900">{plan.title}</h2>
                    {plan.description && <p className="text-slate-500 mt-2">{plan.description}</p>}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => exportDietPlanPDF(plan)}>
                      <Download className="w-4 h-4" /> PDF
                    </Button>
                    <Button
                      size="sm"
                      className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white"
                      onClick={() => handleAiPdf(plan)}
                      disabled={aiPdfLoading === plan.plan_id}
                    >
                      {aiPdfLoading === plan.plan_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4" />
                      )}
                      PDF + IA
                    </Button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 mt-4">
                  {plan.calories_target && (
                    <Badge variant="secondary" className="text-sm px-3 py-1">
                      {plan.calories_target} kcal
                    </Badge>
                  )}
                  {plan.protein_target && (
                    <Badge variant="secondary" className="text-sm px-3 py-1">
                      {plan.protein_target}g protéines
                    </Badge>
                  )}
                  {plan.carbs_target && (
                    <Badge variant="secondary" className="text-sm px-3 py-1">
                      {plan.carbs_target}g glucides
                    </Badge>
                  )}
                  {plan.fat_target && (
                    <Badge variant="secondary" className="text-sm px-3 py-1">
                      {plan.fat_target}g lipides
                    </Badge>
                  )}
                  {plan.water_target && (
                    <Badge variant="secondary" className="text-sm px-3 py-1">
                      {plan.water_target} L eau
                    </Badge>
                  )}
                  {plan.steps_target && (
                    <Badge variant="secondary" className="text-sm px-3 py-1">
                      {plan.steps_target.toLocaleString("fr-BE")} pas
                    </Badge>
                  )}
                </div>

                {plan.meals?.length > 0 && (
                  <div className="mt-6 space-y-4">
                    {plan.meals.map((meal, i) => (
                      <MealAlternativeBlock key={i} plan={plan} meal={meal} mealIndex={i} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}